import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

/**
 * Syncs a dashboard's active tab with the ?tab=X query parameter.
 * Lets notification deep-links like "/buyer?tab=deals" land directly on the right tab.
 *
 * Usage:
 *   const [activeTab, setActiveTab] = useQueryTab('portfolio', ALLOWED_TABS);
 *
 * ALLOWED_TABS is an array of valid tab ids; unknown values fall back to default.
 */
export const useQueryTab = (defaultTab, allowedTabs = null) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const readTab = () => {
    const t = searchParams.get('tab');
    if (!t) return defaultTab;
    if (allowedTabs && !allowedTabs.includes(t)) return defaultTab;
    return t;
  };

  const [activeTab, _setActiveTab] = useState(readTab);

  // If the URL changes (notification click while already on page), re-sync
  useEffect(() => {
    const t = readTab();
    _setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // When tab changes via UI, update URL (replace — don't pollute history)
  const setActiveTab = (tab) => {
    _setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === defaultTab) next.delete('tab');
    else next.set('tab', tab);
    navigate(`${location.pathname}${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
  };

  return [activeTab, setActiveTab];
};

export default useQueryTab;
